import {
	Alert,
	Badge,
	Box,
	Button,
	Stack,
	Heading,
	Inline,
	Icons,
	CheckboxInput,
	useId,
	Text,
	Col,
	Divider,
	Grid,
	joinAttributes,
	Spinner,
	Money,
	Component,
	Time,
} from "@sembark-travel/ui/base"
import { useDialog, Dialog } from "@sembark-travel/ui/dialog"
import {
	addUnit,
	createDuration,
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	isSame,
	localOrUtcTimestampToLocalDate,
	parseDate,
	setTimeFrom,
	startOf,
} from "@sembark-travel/datetime-utils"
import { IListResponse, useXHR } from "@sembark-travel/xhr"
import {
	Form,
	EmptyNumberValidator,
	withServerErrors,
	validateFormValues,
	SubmissionError,
	arrayMutators,
	CheckboxInputField,
	SelectField,
	GetFieldValue,
	TextAreaInputField,
} from "@sembark-travel/ui/form"
import React, { useMemo, useState } from "react"
import useSWR from "swr"
import { Optional } from "utility-types"
import * as Validator from "yup"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { SelectCabs, ICab } from "../Cabs"
import { SelectDriver, TDriver } from "../Drivers"
import {
	SelectTransporServiceProviders,
	ITransportServiceProvider,
} from "../TransportServiceProviders"
import { ICabSchedule, IScheduledCab, ITripCabSchedule } from "./store"
import { MoneyInputWithTenantCurrenciesField } from "../Currencies"
import { Link, queryToSearch } from "@sembark-travel/ui/router"
import { generatePath } from "../router-utils"
import { ScheduleDetailsInDialog } from "./Item"
import {
	getEndDateTimeOfCabSchedule,
	getStartDateTimeOfCabSchedule,
} from "./utils"
import {
	isTicketTypeClosedOnDate,
	showClosedServicesAlert,
} from "../TravelActivities"
import { ShowCabCapacityInfo } from "../CabTypes"

const assignServiceProvidersToSchedulesValidationSchema =
	Validator.object().shape({
		booked: Validator.boolean().nullable(),
		transport_service_provider: Validator.mixed().nullable(),
		driver: Validator.mixed().nullable(),
		cab: Validator.mixed().nullable(),
		// TODO: Validate that atleast one cab is selected
		schedules: Validator.array().of(
			Validator.object().shape({
				booked_price: EmptyNumberValidator()
					.nullable()
					.min(0, "Booking price should be a non-negative value."),
			})
		),
	})

type TAssignServiceProvidersSchema = {
	transport_service_provider?: ITransportServiceProvider
	driver?: TDriver
	cab?: ICab
	schedules: Array<{
		schedule: ICabSchedule
		cabs: Array<{
			cab: IScheduledCab
			cab_id: number
			currency: string
			booked_price?: number
			selected: boolean
			given_price?: number
			remarks?: string
		}>
	}>
	booked: boolean
}

type AssignServiceProvidersProps = {
	tripId: number
	onSuccess: (data: ICabSchedule["cabs"]) => void
	onCancel: () => void
}

function AssignServiceProviders({
	tripId,
	onSuccess,
	onCancel,
}: AssignServiceProvidersProps) {
	const xhr = useXHR()
	const id = useId()
	const { data } = useSWR<ITripCabSchedule>(
		`/trips/${tripId}/cab-schedules?${id}`,
		() =>
			xhr.get(`/trip-cab-schedules/${tripId}`).then((resp) => resp.data.data)
	)
	if (!data) {
		return <Spinner padding="4" alignCenter />
	}
	const { schedules } = data
	if (!schedules.length) {
		return (
			<Box textAlign="center" padding="4">
				No cab schedules for this trip
			</Box>
		)
	}
	return (
		<AssignServiceProvidersToSchedulesForm
			schedules={schedules}
			onCancel={onCancel}
			onSubmit={async (values) => {
				// Transform schedules data to match the required format
				const schedules = values.schedules.flatMap((scheduleItem) =>
					scheduleItem.cabs.map((cabItem) => ({
						currency: cabItem.currency,
						cab_id: cabItem.cab_id,
						booked_price: cabItem.booked_price || 0,
						given_price: cabItem.given_price || 0,
						remarks: cabItem.remarks || "",
						selected: cabItem.selected ? 1 : 0,
					}))
				)

				// Transform remarks to match the required format
				const remarks = values.schedules
					.filter((scheduleItem) => scheduleItem.schedule.remarks)
					.map((scheduleItem) => ({
						cab_schedule_id: scheduleItem.schedule.id,
						remarks: scheduleItem.schedule.remarks,
					}))

				const payload = {
					transport_service_provider: values.transport_service_provider?.id,
					driver: values.driver?.id,
					cab: values.cab?.id,
					booked: values.booked ? 1 : 0,
					remarks: remarks,
					schedules: schedules,
				}

				const resp = await xhr.post(
					"/scheduled-cabs/assign-service-providers",
					payload
				)
				onSuccess(resp.data.data)
			}}
		/>
	)
}

export function AssignServiceProvidersInDialog({
	children,
	onSuccess,
	...props
}: { children: (props: { assign: () => void }) => React.ReactNode } & Optional<
	Omit<AssignServiceProvidersProps, "onCancel">,
	"onSuccess"
>) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ assign: () => open() })}
			<Dialog
				open={isOpen}
				onClose={close}
				title="Assign Service Provider to Schedules"
				xl
			>
				<Dialog.Body>
					<AssignServiceProviders
						onSuccess={(...args) => {
							onSuccess?.(...args)
							close()
						}}
						onCancel={close}
						{...props}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function AssignServiceProvidersToSchedulesForm({
	schedules,
	onCancel,
	onSubmit,
}: {
	schedules: Array<ICabSchedule>
	onSubmit: (
		values: TAssignServiceProvidersSchema & {
			remarks: Array<{ cab_schedule_id: number; remarks: string | undefined }>
		}
	) => Promise<void>
	onCancel: () => void
}) {
	const initialValues: TAssignServiceProvidersSchema = useMemo(() => {
		return {
			transport_service_provider: undefined,
			driver: undefined,
			cab: undefined,
			schedules: schedules.map((schedule) => ({
				schedule: schedule,
				cabs: schedule.cabs.map((cab) => ({
					currency: cab.currency,
					cab: cab,
					cab_id: cab.id,
					booked_price: cab.booked_price,
					selected: false,
					given_price: cab.given_price,
					remarks: cab.remarks,
				})),
			})),
			booked: false,
		}
	}, [schedules])
	const [step, changeStep] = useState(0)
	const { hasPermission } = useCheckPermissions()
	return (
		<Form<TAssignServiceProvidersSchema>
			initialValues={initialValues}
			validate={validateFormValues(
				assignServiceProvidersToSchedulesValidationSchema
			)}
			onSubmit={withServerErrors(async (values) => {
				const closeData: Record<string, Date[]> = {}
				values.schedules.forEach((item) => {
					const {
						schedule: {
							start_date,
							start_date_local,
							transport_service: {
								closed_on_date_intervals,
								closed_on_days_of_week,
								service: serviceName,
							},
						},
					} = item
					if (
						isTicketTypeClosedOnDate(
							{
								closed_on_date_intervals,
								closed_on_days_of_week,
							},
							localOrUtcTimestampToLocalDate(start_date_local, start_date)
						)
					) {
						if (!closeData[serviceName]) closeData[serviceName] = []
						closeData[serviceName].push(
							localOrUtcTimestampToLocalDate(start_date_local, start_date)
						)
					}
				})
				if (Object.keys(closeData).length) {
					const alertHeader = "Some of the services are closed. \n"
					const alertMessage = showClosedServicesAlert(closeData)
					const alertFooter = "\n Please update the services before bookings."
					alert(alertHeader + alertMessage + alertFooter)
					return
				}

				// making schedule remarks array with respective ids
				const remarks: Array<{
					cab_schedule_id: number
					remarks: string | undefined
				}> = values.schedules
					.filter(
						(scheduleItem) =>
							scheduleItem.schedule.remarks &&
							scheduleItem.cabs.some((cab) => cab.selected)
					)
					.map((scheduleItem) => ({
						cab_schedule_id: scheduleItem.schedule.id,
						remarks: scheduleItem.schedule.remarks,
					}))

				const payload = {
					...values,
					remarks,
				}
				return onSubmit(payload)
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, form, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Box>
						{step === 0 ? (
							<Stack gap="4">
								<Stack gap="2">
									<Heading as="h4">Select Cab Service</Heading>
									<Text color="muted">
										Please select applicable services which you want to assign a
										provider to.
									</Text>
								</Stack>
								<Box as="ol">
									<GetFieldValue<TAssignServiceProvidersSchema["schedules"]>
										name={`schedules`}
									>
										{({
											value: schedulesFieldValue,
											onChange: changeSchedulesFieldValue,
										}) => {
											// Count total selected cabs across all schedules
											const totalCabs = schedulesFieldValue.reduce(
												(count, s) => count + s.cabs.length,
												0
											)
											const selectedCabs = schedulesFieldValue.reduce(
												(count, s) =>
													count + s.cabs.filter((c) => c.selected).length,
												0
											)
											return (
												<Box>
													<Box
														as="label"
														padding="4"
														marginBottom="4"
														display="flex"
														alignItems="center"
														bgColor={{ default: "subtle", hover: "inset" }}
														rounded="lg"
														cursor="pointer"
													>
														<CheckboxInput
															checked={selectedCabs === totalCabs}
															onChange={(e) => {
																if (e.currentTarget.checked) {
																	// select all the cabs
																	changeSchedulesFieldValue(
																		schedulesFieldValue.map((s) => ({
																			...s,
																			cabs: s.cabs.map((c) => ({
																				...c,
																				selected: true,
																			})),
																		}))
																	)
																} else {
																	// unselect all the cabs
																	changeSchedulesFieldValue(
																		schedulesFieldValue.map((s) => ({
																			...s,
																			cabs: s.cabs.map((c) => ({
																				...c,
																				selected: false,
																			})),
																		}))
																	)
																}
															}}
														/>
														<Box marginLeft="2" fontWeight="semibold">
															Select All Cabs
														</Box>
													</Box>
													{schedulesFieldValue.map(
														(scheduleItem, scheduleIndex) => {
															const schedule = scheduleItem.schedule
															return (
																<Box
																	key={schedule.id}
																	as="li"
																	paddingX="4"
																	paddingY="2"
																	marginBottom="3"
																	rounded="lg"
																	borderWidth="1"
																	backgroundColor={{ hover: "subtle" }}
																	opacity={schedule.cabs_booked ? "70" : "100"}
																>
																	<Box
																		marginBottom="2"
																		display={{ sm: "flex" }}
																		justifyContent="between"
																	>
																		<Inline gap="2" as="label">
																			<Box>
																				<CheckboxInput
																					checked={scheduleItem.cabs.every(
																						(c) => c.selected
																					)}
																					onChange={(e) => {
																						if (e.currentTarget.checked) {
																							// select all cabs in this schedule
																							changeSchedulesFieldValue(
																								schedulesFieldValue.map(
																									(s, i) =>
																										i === scheduleIndex
																											? {
																													...s,
																													cabs: s.cabs.map(
																														(c) => ({
																															...c,
																															selected: true,
																														})
																													),
																												}
																											: s
																								)
																							)
																						} else {
																							// unselect all cabs in this schedule
																							changeSchedulesFieldValue(
																								schedulesFieldValue.map(
																									(s, i) =>
																										i === scheduleIndex
																											? {
																													...s,
																													cabs: s.cabs.map(
																														(c) => ({
																															...c,
																															selected: false,
																														})
																													),
																												}
																											: s
																								)
																							)
																						}
																					}}
																				/>
																			</Box>
																			<Box>
																				<Box
																					fontWeight="semibold"
																					display="inlineBlock"
																				>
																					{schedule.transport_service.from_to}
																				</Box>
																				<Box display="inline" paddingX="2">
																					-
																				</Box>
																				<Box display="inlineBlock">
																					{schedule.transport_service.service}
																				</Box>
																			</Box>
																		</Inline>
																		<Box>
																			{formatDate(
																				localOrUtcTimestampToLocalDate(
																					schedule.start_date_local,
																					schedule.start_date
																				),
																				"ddd, D MMM"
																			)}
																		</Box>
																	</Box>
																	<Box as="ul" paddingLeft="3">
																		{scheduleItem.cabs.map(
																			(cabItem, cabIndex) => {
																				const cab = cabItem.cab
																				return (
																					<Box
																						key={cab.id}
																						as="li"
																						marginBottom="2"
																						display={{ sm: "flex" }}
																						alignItems="center"
																					>
																						<Box
																							display="flex"
																							alignItems="center"
																							gap={"1"}
																						>
																							<CheckboxInputField
																								label={cab.cab_type.name}
																								name={`schedules[${scheduleIndex}].cabs[${cabIndex}].selected`}
																							/>
																							<ShowCabCapacityInfo
																								cabType={cab.cab_type}
																							/>
																							{cab.booked ? (
																								<Box marginLeft="2">
																									<Badge success>Booked</Badge>
																								</Box>
																							) : cab.transport_service_provider ? (
																								<Box marginLeft="2">
																									<Badge outlined warning>
																										Assigned
																									</Badge>
																								</Box>
																							) : null}
																						</Box>
																						{cab.transport_service_provider ? (
																							<Box
																								marginLeft="4"
																								display="flex"
																								flexWrap="wrap"
																								fontSize="sm"
																								marginTop={{
																									xs: "1",
																									sm: "0",
																								}}
																							>
																								{joinAttributes(
																									<Box>
																										{
																											cab
																												.transport_service_provider
																												.name
																										}
																									</Box>,
																									cab.driver ? (
																										<Box>{cab.driver.name}</Box>
																									) : null,
																									cab.cab ? (
																										<Box>{cab.cab.name}</Box>
																									) : null,
																									cab.cab ? (
																										<Box>
																											{cab.cab.number_plate}
																										</Box>
																									) : null
																								)}
																							</Box>
																						) : null}
																					</Box>
																				)
																			}
																		)}
																	</Box>
																</Box>
															)
														}
													)}
												</Box>
											)
										}}
									</GetFieldValue>
									<Divider sm />
									<Inline gap="4" flexDirection="rowReverse">
										<GetFieldValue<
											TAssignServiceProvidersSchema["schedules"]
										> name="schedules">
											{({ value: schedules }) => {
												const hasSelectedCabs = schedules.some((s) =>
													s.cabs.some((c) => c.selected)
												)
												return (
													<GetFieldValue<
														TAssignServiceProvidersSchema["transport_service_provider"]
													> name="transport_service_provider">
														{({ value: transport_service_provider }) => (
															<Button
																level="primary"
																disabled={!hasSelectedCabs}
																onClick={() => {
																	if (!transport_service_provider) {
																		// get the first service provider from the list
																		let selected_cab_with_tsp:
																			| {
																					cab: IScheduledCab
																					schedule: ICabSchedule
																			  }
																			| undefined
																		for (const scheduleItem of schedules) {
																			const selectedCabItem =
																				scheduleItem.cabs.find(
																					(c) =>
																						c.selected &&
																						c.cab.transport_service_provider
																				)
																			if (selectedCabItem) {
																				selected_cab_with_tsp = {
																					cab: selectedCabItem.cab,
																					schedule: scheduleItem.schedule,
																				}
																				break
																			}
																		}
																		if (selected_cab_with_tsp) {
																			form.change(
																				`transport_service_provider`,
																				selected_cab_with_tsp.cab
																					.transport_service_provider
																			)
																			form.change(
																				`driver`,
																				selected_cab_with_tsp.cab.driver
																			)
																			form.change(
																				`cab`,
																				selected_cab_with_tsp.cab.cab
																			)
																		}
																	}
																	// set the booked attribute if all are booked
																	const allSelectedBooked = schedules.every(
																		(scheduleItem) =>
																			scheduleItem.cabs
																				.filter((c) => c.selected)
																				.every((c) => c.cab.booked)
																	)
																	if (allSelectedBooked) {
																		form.change(`booked`, true)
																	}
																	changeStep(1)
																}}
															>
																Next: Assign Provider{" "}
																<Icons.ChevronDown rotate="270" />
															</Button>
														)}
													</GetFieldValue>
												)
											}}
										</GetFieldValue>
										<Button onClick={onCancel} level="tertiary">
											Cancel
										</Button>
									</Inline>
								</Box>
							</Stack>
						) : null}
						{step === 1 ? (
							<Box>
								<Stack gap="4">
									<Stack gap="1">
										<Heading as="h4">Service Provider and Cab </Heading>
										<Box as="p" color="muted">
											Please provide the service provider with driver and cab
											details if available.
										</Box>
									</Stack>
									<Inline gap="4" flexWrap="wrap">
										<Box maxWidth="sm">
											<SelectField
												label="Service Provider"
												fullWidth
												select={SelectTransporServiceProviders}
												name={`transport_service_provider`}
												creatable
												multiple={false}
												placeholder="Select or Add new..."
												onChange={(
													value: ITransportServiceProvider | undefined
												) => {
													form.batch(() => {
														form.change("transport_service_provider", value)
														if (value?.solo_driver) {
															form.change(`driver`, value.drivers?.[0])
														} else {
															form.change(`driver`, undefined)
														}
														// deselect the cab
														form.change(`cab`, undefined)
													})
												}}
											/>
										</Box>
										<GetFieldValue<
											TAssignServiceProvidersSchema["transport_service_provider"]
										> name="transport_service_provider">
											{({ value: transport_service_provider }) =>
												transport_service_provider ? (
													<>
														{!transport_service_provider.solo_driver ? (
															<Box maxWidth="sm">
																<SelectField
																	label="Driver"
																	select={SelectDriver}
																	name={`driver`}
																	transportServiceProvider={
																		transport_service_provider
																	}
																	options={
																		transport_service_provider.drivers || []
																	}
																	creatable
																/>
															</Box>
														) : null}
														<Box maxWidth="sm">
															<SelectField
																select={SelectCabs}
																label="Cab Details"
																name={`cab`}
																placeholder="RJ18 CC 1212"
																options={transport_service_provider.cabs || []}
																creatable
															/>
														</Box>
													</>
												) : null
											}
										</GetFieldValue>
									</Inline>
								</Stack>
								<Divider />
								<Stack gap="2">
									<Stack gap="1">
										<Heading as="h4">Preview Services</Heading>
										<Box as="p" color="muted">
											Please preview the selected services that will be assigned
											to this provider and set the available booking price.
										</Box>
									</Stack>
									<GetFieldValue<
										TAssignServiceProvidersSchema["schedules"]
									> name="schedules">
										{({ value: schedules }) => (
											<Stack gap="4" as="ol">
												{schedules.map((scheduleItem, scheduleIndex) => {
													const schedule = scheduleItem.schedule
													const selectedCabs = scheduleItem.cabs.filter(
														(c) => c.selected
													)
													if (selectedCabs.length === 0) return null

													return (
														<Box
															key={schedule.id}
															as="li"
															borderWidth="1"
															rounded="lg"
														>
															<Grid>
																<Col
																	xs={12}
																	sm={4}
																	borderRightWidth={{ xs: "0", sm: "1" }}
																	borderBottomWidth={{ xs: "1", sm: "0" }}
																	paddingRight="4"
																	paddingY="4"
																>
																	<Stack gap="2">
																		<Box fontWeight="semibold">
																			{formatDate(
																				localOrUtcTimestampToLocalDate(
																					schedule.start_date_local,
																					schedule.start_date
																				),
																				"ddd, D MMM"
																			)}
																		</Box>
																		<Box fontWeight="semibold">
																			{schedule.transport_service.name}
																		</Box>
																		<GetFieldValue<
																			TAssignServiceProvidersSchema["schedules"]
																		>
																			name={`schedules[${scheduleIndex}].schedule.remarks`}
																		>
																			{({ value: remarks }) => (
																				<Component
																					initialState={remarks ? true : false}
																				>
																					{({ state, setState }) => (
																						<>
																							{!state ? (
																								<Button
																									inline
																									size="sm"
																									align="left"
																									onClick={() => setState(true)}
																								>
																									Add Remarks
																								</Button>
																							) : null}
																							{state ? (
																								<Box>
																									<TextAreaInputField
																										name={`schedules[${scheduleIndex}].schedule.remarks`}
																										label="Customer Remarks"
																										secondaryLabel="optional"
																										rows={2}
																										placeholder="Any service related remarks or notes for the customer."
																									/>
																								</Box>
																							) : null}
																						</>
																					)}
																				</Component>
																			)}
																		</GetFieldValue>

																		{isTicketTypeClosedOnDate(
																			{
																				closed_on_date_intervals:
																					schedule.transport_service
																						.closed_on_date_intervals,
																				closed_on_days_of_week:
																					schedule.transport_service
																						.closed_on_days_of_week,
																			},
																			localOrUtcTimestampToLocalDate(
																				schedule.start_date_local,
																				schedule.start_date
																			)
																		) ? (
																			<Alert
																				status="error"
																				title="Service Closed."
																			>
																				<Text>{`Closed on ${formatDate(
																					localOrUtcTimestampToLocalDate(
																						schedule.start_date_local,
																						schedule.start_date
																					),
																					"dddd, DD MMM"
																				)}.`}</Text>
																			</Alert>
																		) : null}
																	</Stack>
																</Col>

																{/* Right side - Cabs List */}
																<Col xs={12} sm={8} paddingY="4">
																	<Stack gap="3">
																		{scheduleItem.cabs.map(
																			(cabItem, cabIndex) => {
																				if (!cabItem.selected) return null
																				const cab = cabItem.cab
																				return (
																					<Box
																						key={cab.id}
																						borderBottomWidth={
																							cabIndex ===
																							scheduleItem.cabs.length - 1
																								? undefined
																								: "1"
																						}
																						paddingBottom={
																							cabIndex ===
																							scheduleItem.cabs.length - 1
																								? undefined
																								: "4"
																						}
																					>
																						<Grid noGutters>
																							<Col xs={12} sm={6}>
																								<Stack gap="1">
																									<Inline
																										fontWeight="semibold"
																										gap="2"
																										alignItems="center"
																									>
																										<Box
																											paddingX="2"
																											paddingY="1"
																											bgColor="primary"
																											borderRadius="full"
																										>
																											<Icons.Taxi
																												size="4"
																												color="primary"
																											/>
																										</Box>{" "}
																										{cab.cab_type.name}
																										<ShowCabCapacityInfo
																											cabType={cab.cab_type}
																										/>
																									</Inline>
																									<Box
																										fontSize="sm"
																										color="muted"
																									>
																										{joinAttributes(
																											`ID: ${cab.id}`,
																											cab.booked ? (
																												<Badge success>
																													Booked
																												</Badge>
																											) : null
																										)}
																									</Box>
																								</Stack>
																							</Col>
																							<Col xs={12} sm={3}>
																								{hasPermission(
																									PERMISSIONS.MANAGE_TRIP_OWNERS
																								) &&
																								cabItem.given_price !==
																									undefined &&
																								cabItem.given_price !== null ? (
																									<Box
																										paddingTop="6"
																										color="muted"
																									>
																										Selling Price:{" "}
																										<Money
																											amount={
																												cabItem.given_price
																											}
																											currency={
																												cabItem.currency
																											}
																										/>
																									</Box>
																								) : null}
																							</Col>
																							<Col xs={12} sm={3}>
																								<MoneyInputWithTenantCurrenciesField
																									label="Price"
																									name={`schedules[${scheduleIndex}].cabs[${cabIndex}].booked_price`}
																									currencyFieldName={`schedules[${scheduleIndex}].cabs[${cabIndex}].currency`}
																								/>
																							</Col>
																							<Col xs={12}>
																								<GetFieldValue<
																									TAssignServiceProvidersSchema["schedules"][number]["cabs"][number]
																								>
																									name={`schedules[${scheduleIndex}].cabs[${cabIndex}]`}
																								>
																									{({ value: cab }) => {
																										if (
																											!cab.cab.cab_type
																												.display_name
																										)
																											return null

																										return (
																											<Component
																												initialState={
																													!!cab.remarks
																												}
																											>
																												{({
																													state,
																													setState,
																												}: {
																													state: boolean
																													setState: (
																														newState: boolean
																													) => void
																												}) => (
																													<>
																														{!state ? (
																															<Button
																																inline
																																size="sm"
																																align="left"
																																onClick={() =>
																																	setState(true)
																																}
																															>
																																Add Remarks
																															</Button>
																														) : null}
																														{state ? (
																															<Stack gap="1">
																																<TextAreaInputField
																																	name={`schedules[${scheduleIndex}].cabs[${cabIndex}].remarks`}
																																	label="Cabwise Remarks"
																																	secondaryLabel="optional"
																																	rows={2}
																																	placeholder="Any notes/remarks that should be shared with Supplier and Customer..."
																																/>
																																<Inline
																																	gap="1"
																																	color="muted"
																																	fontSize="sm"
																																	alignItems="center"
																																>
																																	<Icons.Info size="3" />
																																	<Text>
																																		This remark
																																		applies only
																																		to this cab
																																	</Text>
																																</Inline>
																															</Stack>
																														) : null}
																													</>
																												)}
																											</Component>
																										)
																									}}
																								</GetFieldValue>
																							</Col>
																						</Grid>

																						{/* Warnings */}
																						<GetFieldValue<
																							TAssignServiceProvidersSchema["driver"]
																						> name="driver">
																							{({ value: driver }) => (
																								<GetFieldValue<
																									TAssignServiceProvidersSchema["cab"]
																								> name="driver">
																									{({ value: formCab }) =>
																										driver || formCab ? (
																											scheduleItem.cabs.some(
																												(c) =>
																													c.selected &&
																													c.cab_id !==
																														cabItem.cab_id
																											) ? (
																												<Alert
																													status="warning"
																													title="Single driver/cab assigned to multiple cabs for single service."
																													rounded="none"
																												>
																													<Box as="p">
																														You are assigning a
																														single driver/cab (
																														{[
																															driver?.name,
																															formCab?.number_plate,
																														]
																															.filter(Boolean)
																															.join(", ")}
																														) to multiple cabs
																														for a single service
																														(
																														{
																															schedule
																																.transport_service
																																.name
																														}
																														).
																													</Box>
																													<Box as="p">
																														A driver/cab should
																														be assigned to a
																														single cab service
																														for a given
																														transport service.
																													</Box>
																												</Alert>
																											) : null
																										) : null
																									}
																								</GetFieldValue>
																							)}
																						</GetFieldValue>

																						<GetFieldValue<
																							TAssignServiceProvidersSchema["transport_service_provider"]
																						> name="transport_service_provider">
																							{({ value: supplier }) => (
																								<GetFieldValue<
																									TAssignServiceProvidersSchema["driver"]
																								> name="driver">
																									{({ value: driver }) =>
																										!supplier ? null : (
																											<WarnForExistingScheduledCabsForSupplier
																												tripId={
																													schedule.trip_id
																												}
																												scheduledCabId={cab.id}
																												startDate={localOrUtcTimestampToLocalDate(
																													schedule.start_date_local,
																													schedule.start_date
																												)}
																												startTime={
																													schedule.start_time_local
																														? parseDate(
																																schedule.start_time_local,
																																"HH:mm:ss"
																															)
																														: undefined
																												}
																												duration={createDuration(
																													schedule.duration_iso
																												).asMinutes()}
																												supplier={supplier}
																												driver={driver}
																											/>
																										)
																									}
																								</GetFieldValue>
																							)}
																						</GetFieldValue>
																					</Box>
																				)
																			}
																		)}
																	</Stack>
																</Col>
																<Col xs={12}>
																	{/* Full width alerts that apply to whole schedule */}
																	<GetFieldValue<
																		TAssignServiceProvidersSchema["driver"]
																	> name="driver">
																		{({ value: driver }) => (
																			<GetFieldValue<
																				TAssignServiceProvidersSchema["cab"]
																			> name="driver">
																				{({ value: formCab }) => {
																					const multipleSelectedCabs =
																						scheduleItem.cabs.filter(
																							(c) => c.selected
																						).length > 1
																					return driver || formCab ? (
																						multipleSelectedCabs ? (
																							<Box marginTop="3">
																								<Alert
																									status="warning"
																									title="Single driver/cab assigned to multiple cabs for single service."
																								>
																									<Box as="p">
																										You are assigning a single
																										driver/cab (
																										{[
																											driver?.name,
																											formCab?.number_plate,
																										]
																											.filter(Boolean)
																											.join(", ")}
																										) to multiple cabs for
																										service (
																										{
																											schedule.transport_service
																												.name
																										}
																										).
																									</Box>
																								</Alert>
																							</Box>
																						) : null
																					) : null
																				}}
																			</GetFieldValue>
																		)}
																	</GetFieldValue>
																</Col>
															</Grid>
														</Box>
													)
												})}
											</Stack>
										)}
									</GetFieldValue>
								</Stack>
								<Divider sm />
								<Box marginTop="4" padding="6" bgColor="subtle" rounded="lg">
									<CheckboxInputField
										name={`booked`}
										label="Confirmed by Supplier"
										help="Mark attached cabs as booked"
									/>
								</Box>
								<Divider sm />
								<Stack gap="4">
									<SubmissionError />
									<Box
										display={{ sm: "flex" }}
										flexDirection="rowReverse"
										justifyContent="between"
										gap="4"
									>
										<Inline flexDirection="rowReverse" gap="4">
											<Button type="submit" disabled={submitting}>
												{submitting ? "Assigning" : "Assign"}
											</Button>
											<Button
												onClick={() => onCancel()}
												level="tertiary"
												disabled={submitting}
											>
												Cancel
											</Button>
										</Inline>
										<Box>
											<Button onClick={() => changeStep(0)}>
												<Icons.ChevronDown rotate="90" /> Edit Services
											</Button>
										</Box>
									</Box>
								</Stack>
							</Box>
						) : null}
					</Box>
				</form>
			)}
		</Form>
	)
}

export function WarnForExistingScheduledCabsForSupplier({
	startDate,
	startTime,
	duration,
	tripId,
	scheduledCabId: existingId,
	supplier,
	driver,
}: {
	supplier: ITransportServiceProvider
	driver?: TDriver
	startDate: Date
	startTime?: Date | string
	duration?: number
	tripId?: number | string
	scheduledCabId?: number
}) {
	const xhr = useXHR()
	const pickupDateStartOfDay = useMemo(() => {
		return startOf(startDate, "day")
	}, [startDate])

	const startDateTimeParsed = useMemo(() => {
		if (startTime) {
			const time = parseDate(startTime, "HH:mm")
			return setTimeFrom(startDate, time)
		}
		if (formatDate(startDate, "HH:mm") !== "00:01") {
			return startDate
		}
		return undefined
	}, [startDate, startTime])

	const endDateTimeParsed = useMemo(() => {
		if (!duration || !startTime || !startDateTimeParsed)
			return startDateTimeParsed
		return addUnit(startDateTimeParsed, Number(duration), "minute")
	}, [startTime, duration, startDateTimeParsed])

	const dropDateEndOfDay = useMemo(() => {
		return endOf(endDateTimeParsed || pickupDateStartOfDay, "day")
	}, [pickupDateStartOfDay, endDateTimeParsed])

	const { data } = useSWR(
		supplier &&
			pickupDateStartOfDay &&
			dropDateEndOfDay &&
			(!supplier.solo_driver ? driver : true)
			? `/existing-scheduled-cabs-for-driver${queryToSearch({
					pickup_after: pickupDateStartOfDay.toISOString(),
					pickup_before: dropDateEndOfDay.toISOString(),
					supplier_id: supplier.id,
					existing_id: existingId,
					driver_id: driver?.id,
				})}`
			: null,
		function () {
			if (!supplier || !pickupDateStartOfDay || !dropDateEndOfDay) {
				return undefined
			}
			return xhr
				.get<IListResponse<IScheduledCab>>("/scheduled-cabs", {
					params: {
						pickup_after: dateToUTCString(pickupDateStartOfDay),
						pickup_before: dateToUTCString(dropDateEndOfDay),
						except_schedules: existingId ? [existingId] : undefined,
						drivers: driver ? [driver.id] : undefined,
						transportServiceProviders: [supplier.id],
						limit: 5,
					},
				})
				.then((resp) => resp.data)
		}
	)

	if (!data || !data.data.length) {
		return null
	}

	const { data: scheduledCabs, meta } = data
	const pickupAndDropSameDay = isSame(
		pickupDateStartOfDay,
		dropDateEndOfDay,
		"day"
	)
	return (
		<Alert
			status="warning"
			title={`Multiple bookings for "${supplier?.name}" ${driver ? ` with driver "${driver.name}"` : null} for this date`}
		>
			<Stack gap="4">
				<Text>
					Following schedules have already been assigned to this driver{" "}
					{pickupAndDropSameDay ? (
						<>
							on <Time value={pickupDateStartOfDay} />
						</>
					) : (
						<>
							during <Time value={pickupDateStartOfDay} /> and{" "}
							<Time value={dropDateEndOfDay} />
						</>
					)}
				</Text>
				<Stack
					as="ol"
					gap="1"
					bgColor="default"
					color="default"
					rounded="md"
					borderWidth="1"
				>
					{scheduledCabs.map((s) => {
						const { cab_schedule, cab_type, cab } = s
						const startDateAndTime = cab_schedule
							? getStartDateTimeOfCabSchedule(cab_schedule)
							: undefined
						const endDateAndTime = cab_schedule
							? getEndDateTimeOfCabSchedule(cab_schedule)
							: undefined
						return (
							<Box as="li" key={s.id} paddingX="4" paddingY="2">
								<Inline gap="4" justifyContent="between">
									<Stack gap="px">
										<Text fontWeight="semibold">
											{cab_schedule?.transport_service.name} - [
											{cab ? cab.full_name : cab_type.name}] - #{s.id}{" "}
										</Text>
										{cab_schedule ? (
											<Box fontSize={"sm"} color="muted">
												{joinAttributes(
													String(tripId) === String(cab_schedule.trip_id) ? (
														`This Trip# ${tripId}`
													) : (
														<Link
															to={generatePath(
																"/trips/:tripId/services-bookings/:serviceType",
																{
																	tripId: cab_schedule.trip_id.toString(),
																	serviceType: "operations",
																}
															)}
														>
															Trip# {cab_schedule.trip_id}
														</Link>
													),
													startDateAndTime ? (
														<>
															{formatDate(startDateAndTime, "DD MMM HH:mm")} hrs
															{endDateAndTime &&
															!isSame(startDateAndTime, endDateAndTime)
																? ` - ${formatDate(endDateAndTime, "DD MMM HH:mm")} hrs`
																: ``}
														</>
													) : null
												)}
											</Box>
										) : null}
									</Stack>
									{cab_schedule ? (
										<Box>
											<ScheduleDetailsInDialog
												schedule={cab_schedule}
												disableEdit
											>
												{({ onShow }) => (
													<Button
														size="sm"
														onClick={() => onShow()}
														status="primary"
													>
														<Icons.ArrowExpand />
													</Button>
												)}
											</ScheduleDetailsInDialog>
										</Box>
									) : null}
								</Inline>
							</Box>
						)
					})}
				</Stack>
				<Text fontWeight="semibold">
					{joinAttributes(
						<>Total: {meta.total}</>,
						supplier && pickupDateStartOfDay ? (
							<Link
								target="_blank"
								to={generatePath("/operational-bookings/date/:date", {
									date: dateToQuery(pickupDateStartOfDay),
								})}
								query={{
									tsps: [`${supplier.id}_${supplier.name}`],
									drivers: driver ? [`${driver.id}_${driver.name}`] : [],
								}}
							>
								View All <Icons.ArrowTopRightOnSquare />
							</Link>
						) : null
					)}
				</Text>
			</Stack>
		</Alert>
	)
}
